import { NativeStackScreenProps } from '@react-navigation/native-stack';
import { AssetSummaryPage } from 'components/Asset/AssetSummaryPage';
import { NavigationStacks, Screens, Stacks } from 'components/Navigation/types';
import { useScreenSize } from 'hooks/useScreenSize';
import { useSelector } from 'hooks/useSelector';
import { orderBy as naturalOrderBy } from 'natural-orderby';
import * as React from 'react';
import { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { SET_ASSET_DELTA } from 'store/actions';
import { selectAsset } from 'store/assets/selectors';
import { Asset } from 'store/assets/types';
import {
  selectUserOrganisationFeatures,
  selectUserOrganisationUuidToUid,
} from 'store/auth/selectors';
import { selectOrganisation } from 'store/organisations/selectors';
import { AppContextEnum } from 'types/types';

type Props = NativeStackScreenProps<
  NavigationStacks[Stacks.ASSET],
  Screens.ASSET_SUMMARY
>;

export const AssetSummaryScreen: React.FC<Props> = ({ navigation, route }) => {
  const { assetId } = route.params;

  const dispatch = useDispatch();
  const screenSize = useScreenSize();

  const asset = useSelector((state) => selectAsset(state, assetId));
  const organisation = useSelector((state) =>
    selectOrganisation(state, asset.organisation_id),
  );

  const organisationUid = useSelector((state) =>
    selectUserOrganisationUuidToUid(state, asset.organisation_id),
  );
  const userFeatures = useSelector((state) =>
    selectUserOrganisationFeatures(state, organisationUid),
  );

  const handleChanges = useCallback(
    (
      data: Partial<
        Record<keyof Asset['details'] | 'customReference', string | null>
      >,
    ) => {
      const details: Partial<Asset['details']> = Object.fromEntries(
        Object.entries(data).map(([key, value]) => [
          key === 'customReference' ? 'custom_reference' : key,
          value || '',
        ]),
      );
      dispatch(
        SET_ASSET_DELTA({
          delta: {
            details,
          },
          id: assetId,
        }),
      );
    },
    [assetId, dispatch],
  );

  const teams = naturalOrderBy(
    Object.entries(asset.team_responsibles).reduce<
      { id: string; name: string; people: string }[]
    >((acc, [teamId, accountIds]) => {
      const team = organisation.teams[teamId];
      if (team) {
        acc.push({
          id: team.id,
          name: team.name,
          people: naturalOrderBy(
            accountIds.reduce<string[]>((acc, accountId) => {
              const account = organisation.accounts[accountId];
              if (account) {
                acc.push(account.name);
              }
              return acc;
            }, []),
          ).join(', '),
        });
      }
      return acc;
    }, []),
    (v) => v.name,
  );

  return (
    <AssetSummaryPage
      isModal={false}
      context={{
        context: AppContextEnum.localAsset,
        assetId,
      }}
      editable={{
        avatar: userFeatures.editassetdetails,
        name: userFeatures.editassetdetails,
        customReference: userFeatures.editassetdetails,
        address: userFeatures.editassetdetails,
      }}
      onChanges={handleChanges}
      onPressBackButton={screenSize === 'small' ? navigation.goBack : undefined}
      details={{
        name: asset.details.name,
        reference: asset.reference,
        avatar: asset.details.avatar || '',
        customReference: asset.details.custom_reference || '',
        parentName: asset.details.parentName || '',
        address1: asset.details.address1 || '',
        address2: asset.details.address2 || '',
        city: asset.details.city || '',
        county: asset.details.county || '',
        postcode: asset.details.postcode || '',
        phone: asset.details.phone || '',
        teams,
        organisationName: organisation.details.name,
      }}
    />
  );
};
